import type { Call } from "@frontierx/types";
import { addHours, isAfter, isBefore, subDays } from "date-fns";

export interface CategorizedCalls {
  now: Call[];
  upNext: Call[];
  recent: Call[];
}

/**
 * Categorizes calls into Now, Up Next, and Recent for the desktop app dashboard
 */
export function categorizeCalls(
  calls: Call[],
  currentTime?: Date,
): CategorizedCalls {
  const now = currentTime ?? new Date();
  const eightHoursFromNow = addHours(now, 8); // 8 hours window for "Up Next"
  const sevenDaysAgo = subDays(now, 7);

  console.log("[web/categorizeCalls] Starting categorization", {
    totalCalls: calls.length,
    now: now.toISOString(),
    eightHoursFromNow: eightHoursFromNow.toISOString(),
    sevenDaysAgo: sevenDaysAgo.toISOString(),
  });

  const now_calls: Call[] = [];
  const upNext: Call[] = [];
  const recent: Call[] = [];

  for (const call of calls) {
    const startTime = new Date(call.startTime);
    const callInfo = {
      id: call.id,
      status: call.status,
      startTime: call.startTime,
      callEndedAt: call.callEndedAt,
    };

    // NOW: Currently active calls (in progress or should be happening now)
    if (isCurrentlyActive(call, now)) {
      console.log("[web/categorizeCalls] → NOW", callInfo);
      now_calls.push(call);
    }
    // UP NEXT: Scheduled calls starting within next 8 hours
    else if (
      call.status === "scheduled" &&
      isAfter(startTime, now) &&
      isBefore(startTime, eightHoursFromNow)
    ) {
      console.log("[web/categorizeCalls] → UP NEXT", callInfo);
      upNext.push(call);
    }
    // SAFETY NET: Scheduled calls that have passed start time but aren't marked as active
    // This prevents calls from disappearing during status transitions
    else if (
      call.status === "scheduled" &&
      startTime.getTime() <= now.getTime() &&
      !call.callEndedAt &&
      // Only include if it started within the last 2 hours (reasonable window)
      isBefore(now, addHours(startTime, 2))
    ) {
      console.log("[web/categorizeCalls] → NOW (safety net)", callInfo);
      now_calls.push(call);
    }
    // RECENT: Completed calls from last 7 days
    else if (
      isRecentlyCompleted(call, sevenDaysAgo) &&
      !now_calls.some((nowCall) => nowCall.id === call.id) &&
      !upNext.some((nextCall) => nextCall.id === call.id)
    ) {
      console.log("[web/categorizeCalls] → RECENT", callInfo);
      recent.push(call);
    } else {
      console.log("[web/categorizeCalls] → FILTERED OUT", {
        ...callInfo,
        reasons: {
          isCurrentlyActive: isCurrentlyActive(call, now),
          isScheduled: call.status === "scheduled",
          isAfterNow: isAfter(startTime, now),
          isBeforeEightHours: isBefore(startTime, eightHoursFromNow),
          isRecentlyCompleted: isRecentlyCompleted(call, sevenDaysAgo),
        },
      });
    }
  }

  // Sort each category
  now_calls.sort(
    (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
  );
  upNext.sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );
  recent.sort(
    (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
  );

  console.log("[web/categorizeCalls] Categorization complete", {
    nowCount: now_calls.length,
    upNextCount: upNext.length,
    recentCount: recent.length,
    upNextLimited: upNext.slice(0, 3).length,
    recentLimited: recent.slice(0, 5).length,
    nowSample: now_calls
      .slice(0, 2)
      .map((c) => ({ id: c.id, status: c.status, startTime: c.startTime })),
    upNextSample: upNext
      .slice(0, 2)
      .map((c) => ({ id: c.id, status: c.status, startTime: c.startTime })),
    recentSample: recent
      .slice(0, 2)
      .map((c) => ({ id: c.id, status: c.status, startTime: c.startTime })),
  });

  return {
    now: now_calls,
    upNext: upNext.slice(0, 3), // Limit to max 3 upcoming calls
    recent: recent.slice(0, 5), // Limit recent to 5 calls
  };
}

/**
 * Determines if a call is currently active
 */
function isCurrentlyActive(call: Call, now: Date): boolean {
  const startTime = new Date(call.startTime);
  const actualEndTime = call.callEndedAt ? new Date(call.callEndedAt) : null;

  // If call has actually ended (callEndedAt is set), it's never active regardless of status
  if (actualEndTime) {
    return false;
  }

  // Check status-based activity
  if (call.status === "in_progress") {
    return true;
  }

  // Check time-based activity for scheduled calls
  if (call.status === "scheduled") {
    // If call hasn't actually ended, check if it should be active based on start time
    if (startTime.getTime() <= now.getTime()) {
      // Call has started - since callEndedAt is null, keep it active
      // Use a reasonable default window (2 hours) regardless of plannedEndTime
      // This prevents calls from disappearing when they run over the planned time
      const twoHoursFromStart = addHours(startTime, 2);
      return isBefore(now, twoHoursFromStart);
    }
  }

  return false;
}

/**
 * Determines if a call was recently completed
 */
function isRecentlyCompleted(call: Call, cutoffDate: Date): boolean {
  const completedStatuses = ["processed"];

  // Include calls that have actually ended (callEndedAt is set) even if not yet processed
  // This prevents recently ended calls from disappearing while waiting for processing
  const hasActuallyEnded = !!call.callEndedAt;
  const isProcessed = completedStatuses.includes(call.status);

  if (!isProcessed && !hasActuallyEnded) {
    return false;
  }

  // Use actual end time if available, then planned end time, otherwise start time
  const referenceTime = call.callEndedAt
    ? new Date(call.callEndedAt)
    : call.endTime
      ? new Date(call.endTime)
      : new Date(call.startTime);

  return isAfter(referenceTime, cutoffDate);
}

/**
 * Gets display text for call status in the desktop app
 */
export function getDesktopStatusText(call: Call): string {
  switch (call.status) {
    case "scheduled":
      return "Scheduled";
    case "in_progress":
      return "Live";
    case "processed":
      return "Completed";
    case "failed":
    case "cancelled":
      return "Ended";
    case "pending_scheduling":
      return "Scheduling";
    case "scheduling_failed":
    case "oauth_failed":
      return "Failed";
    default:
      return call.status;
  }
}

/**
 * Determines if a call can be joined (has meeting URL and is scheduled/active)
 */
export function canJoinCall(call: Call): boolean {
  return !!(
    call.meetingUrl &&
    (call.status === "scheduled" || call.status === "in_progress")
  );
}

/**
 * Determines if a call can be rejoined via Frontier AI
 * This is for calls that users created and might want to return to
 */
export function canRejoinCall(call: Call): boolean {
  // Allow rejoining if it's a recent call (within last 2 hours) regardless of status
  // or if it has a meeting URL and isn't explicitly ended
  const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);
  const now = new Date();
  const callTime = new Date(call.startTime);

  const isRecent = callTime > twoHoursAgo && callTime <= now;
  const hasActiveUrl = !!(
    call.meetingUrl && !["ended", "cancelled"].includes(call.status)
  );

  return isRecent || hasActiveUrl;
}
